import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { clsx } from 'clsx';
import Button from 'components/Button/Button';
import useMonitoringResults from 'components/MonitoringAgent/hooks/useMonitoringResults';
import SidePanel from 'components/SidePanel/SidePanel';
import SmallLoader from 'components/SmallLoader/SmallLoader';
import { BUTTON_VARIANT } from 'constants/interfaces';
import {
  isClimateMonitoringSignalResult,
  isFinanceMonitoringSignalResult,
  isOwnershipMonitoringSignalResult,
  isRegulationMonitoringSignalResult,
  isReputationMonitoringSignalResult,
} from 'constants/interfaces/IMonitoringSignalResult';

import {
  filterClimateFindingsByTheme,
  getClimateAtRiskUnitIdsFromFindings,
  sortClimateFindings,
  sortFinanceFindings,
  sortMonitoringArticles,
  sortOwnershipFindings,
  sortRegulationSignals,
} from '../utils/monitoringResultsUtils';
import ClimateAnalysisModal from './ClimateAnalysisModal';
import MonitoringArticleCard from './MonitoringArticleCard';
import MonitoringClimateEntityCard from './MonitoringClimateEntityCard';
import MonitoringClimateFindingCard from './MonitoringClimateFindingCard';
import MonitoringFinanceEntityCard from './MonitoringFinanceEntityCard';
import MonitoringFinanceFindingCard from './MonitoringFinanceFindingCard';
import MonitoringOwnershipEntityCard from './MonitoringOwnershipEntityCard';
import MonitoringOwnershipFindingCard from './MonitoringOwnershipFindingCard';
import MonitoringRegulationCard from './MonitoringRegulationCard';
import MonitoringResultsSummary from './MonitoringResultsSummary';
import OwnershipAnalysisModal from './OwnershipAnalysisModal';

import type IMonitoringResultsPanel from './interfaces/IMonitoringResultsPanel';

import styles from './sass/MonitoringResultsPanel.module.scss';

function MonitoringResultsPanel({
  open,
  titleKey,
  sessionTitle,
  valueChainMapId,
  sessionId,
  runId,
  agentDefinition,
  fallbackEntities = [],
  breadcrumbMode = 'session',
  breadcrumbScheduleTitle,
  breadcrumbRunLabel,
  showImpactScenarioCta = false,
  isImpactScenarioCreating = false,
  onCreateImpactScenario,
  onBack,
  onClose,
}: IMonitoringResultsPanel): React.JSX.Element {
  const { t } = useTranslation();
  const isRegulationMode = agentDefinition.resultsMode === 'regulations';
  const isFinanceMode = agentDefinition.resultsMode === 'financial-risk';
  const isOwnershipMode = agentDefinition.resultsMode === 'ownership';
  const isClimateMode = agentDefinition.resultsMode === 'climate';
  const [financeThemeFilter, setFinanceThemeFilter] = useState<string>('all');
  const [ownershipThemeFilter, setOwnershipThemeFilter] = useState<string>('all');
  const [ownershipResultsTab, setOwnershipResultsTab] = useState<'profiles' | 'findings'>('profiles');
  const [isOwnershipAnalysisOpen, setIsOwnershipAnalysisOpen] = useState<boolean>(false);
  const [climateThemeFilter, setClimateThemeFilter] = useState<string>('all');
  const [climateResultsTab, setClimateResultsTab] = useState<'profiles' | 'findings'>('profiles');
  const [isClimateAnalysisOpen, setIsClimateAnalysisOpen] = useState<boolean>(false);

  const getImpactCtaKey = () => {
    if (isFinanceMode) {
      return 'valueChainMap.monitoring.finance.impactBridgeCta';
    }

    if (isRegulationMode) {
      return 'valueChainMap.monitoring.regulation.impactBridgeCta';
    }

    if (isOwnershipMode) {
      return 'valueChainMap.monitoring.ownership.impactBridgeCta';
    }

    if (isClimateMode) {
      if (climateThemeFilter !== 'all') {
        return 'valueChainMap.monitoring.climate.impactBridgeCtaFiltered';
      }

      return 'valueChainMap.monitoring.climate.impactBridgeCta';
    }

    return 'valueChainMap.monitoring.reputation.impactBridgeCta';
  };

  const getImpactCtaDescriptionKey = () => {
    if (isFinanceMode) {
      return 'valueChainMap.monitoring.finance.impactBridgeCtaDescription';
    }

    if (isRegulationMode) {
      return 'valueChainMap.monitoring.regulation.impactBridgeCtaDescription';
    }

    if (isOwnershipMode) {
      return 'valueChainMap.monitoring.ownership.impactBridgeCtaDescription';
    }

    if (isClimateMode) {
      if (climateThemeFilter !== 'all') {
        return 'valueChainMap.monitoring.climate.impactBridgeCtaFilteredDescription';
      }

      return 'valueChainMap.monitoring.climate.impactBridgeCtaDescription';
    }

    return 'valueChainMap.monitoring.reputation.impactBridgeCtaDescription';
  };

  const impactCtaKey = getImpactCtaKey();
  const impactCtaDescriptionKey = getImpactCtaDescriptionKey();

  const {
    results,
    isLoading,
    isError,
    isEmpty,
  } = useMonitoringResults({
    valueChainMapId,
    sessionId,
    runId,
    isEnabled: open,
    resultsMode: agentDefinition.resultsMode,
    fallbackEntities,
  });

  const sortedArticles = isReputationMonitoringSignalResult(results)
    ? sortMonitoringArticles(results.articles_found)
    : [];
  const sortedRegulations = isRegulationMonitoringSignalResult(results)
    ? sortRegulationSignals(results.regulations_found)
    : [];
  const financeFindings = useMemo(
    () => (
      isFinanceMonitoringSignalResult(results)
        ? sortFinanceFindings(results.findings)
        : []
    ),
    [results],
  );
  const financeEntities = isFinanceMonitoringSignalResult(results)
    ? results.entities
    : [];

  const financeThemeOptions = useMemo(() => {
    const themes = Array.from(new Set(financeFindings.map((finding) => finding.theme)));

    return ['all', ...themes];
  }, [financeFindings]);

  const filteredFinanceFindings = financeThemeFilter === 'all'
    ? financeFindings
    : financeFindings.filter((finding) => finding.theme === financeThemeFilter);

  const ownershipFindings = useMemo(
    () => (
      isOwnershipMonitoringSignalResult(results)
        ? sortOwnershipFindings(results.findings)
        : []
    ),
    [results],
  );
  const ownershipEntities = isOwnershipMonitoringSignalResult(results)
    ? results.entities
    : [];

  const ownershipThemeOptions = useMemo(() => {
    const themes = Array.from(new Set(ownershipFindings.map((finding) => finding.theme)));

    return ['all', ...themes];
  }, [ownershipFindings]);

  const filteredOwnershipFindings = ownershipThemeFilter === 'all'
    ? ownershipFindings
    : ownershipFindings.filter((finding) => finding.theme === ownershipThemeFilter);

  const climateFindings = useMemo(
    () => (
      isClimateMonitoringSignalResult(results)
        ? sortClimateFindings(results.findings)
        : []
    ),
    [results],
  );
  const climateEntities = useMemo(
    () => (
      isClimateMonitoringSignalResult(results)
        ? results.entities
        : []
    ),
    [results],
  );

  const climateThemeOptions = useMemo(() => {
    const themes = Array.from(new Set(climateFindings.map((finding) => finding.theme)));

    return ['all', ...themes];
  }, [climateFindings]);

  const filteredClimateFindings = filterClimateFindingsByTheme(climateFindings, climateThemeFilter);

  const filteredClimateEntities = useMemo(() => {
    if (climateThemeFilter === 'all') {
      return climateEntities;
    }

    const unitIds = new Set(filteredClimateFindings.map((finding) => finding.entity_unit_id));

    return climateEntities.filter((entity) => (
      unitIds.has(entity.unitId)
      || (entity.climate_profile.hazards_active || []).includes(climateThemeFilter)
    ));
  }, [climateEntities, climateThemeFilter, filteredClimateFindings]);

  const climateFilteredAtRiskUnitIds = useMemo(() => {
    if (!isClimateMode) {
      return undefined;
    }

    const monitoredIds = fallbackEntities.map((entity) => entity.unitId);
    const atRisk = getClimateAtRiskUnitIdsFromFindings(
      filteredClimateFindings,
      monitoredIds.length ? monitoredIds : climateEntities.map((entity) => entity.unitId),
      fallbackEntities.length
        ? fallbackEntities
        : climateEntities.map((entity) => ({ unitId: entity.unitId, name: entity.name })),
    );

    return Array.from(atRisk);
  }, [climateEntities, filteredClimateFindings, fallbackEntities, isClimateMode]);

  const getEmptyMessageKey = () => {
    if (isFinanceMode) {
      return 'valueChainMap.monitoring.results.noFinanceFindings';
    }

    if (isRegulationMode) {
      return 'valueChainMap.monitoring.results.noRegulations';
    }

    if (isOwnershipMode) {
      return 'valueChainMap.monitoring.results.noOwnershipFindings';
    }

    if (isClimateMode) {
      return 'valueChainMap.monitoring.results.noClimateFindings';
    }

    return 'valueChainMap.monitoringResultsEmpty';
  };

  const emptyMessageKey = getEmptyMessageKey();

  const renderBreadcrumb = () => {
    if (breadcrumbMode === 'schedule-run' && breadcrumbScheduleTitle && breadcrumbRunLabel) {
      return (
        <nav className={styles.breadcrumb} aria-label={t('valueChainMap.monitoring.results.breadcrumbLabel')}>
          <span>{t('valueChainMap.monitoringHubSchedulesTab')}</span>
          <span className={styles.breadcrumbSeparator} aria-hidden="true">→</span>
          <span>{breadcrumbScheduleTitle}</span>
          <span className={styles.breadcrumbSeparator} aria-hidden="true">→</span>
          <span>{breadcrumbRunLabel}</span>
        </nav>
      );
    }

    if (!sessionTitle) {
      return null;
    }

    return (
      <nav className={styles.breadcrumb} aria-label={t('valueChainMap.monitoring.results.breadcrumbLabel')}>
        <span>{t('valueChainMap.monitoringHubSessionsTab')}</span>
        <span className={styles.breadcrumbSeparator} aria-hidden="true">→</span>
        <span>{sessionTitle}</span>
      </nav>
    );
  };

  const renderFinanceBody = () => (
    <>
      <MonitoringResultsSummary
        summary={isFinanceMonitoringSignalResult(results) ? results.summary : {
          total_findings: 0,
          high_risk_count: 0,
          entities_monitored: 0,
        }}
        mode="financial-risk"
      />

      {financeEntities.length ? (
        <div className={styles.articleList}>
          <h3 className={styles.sectionTitle}>
            {t('valueChainMap.monitoring.results.financeEntitiesSection')}
          </h3>
          {financeEntities.map((entity) => (
            <MonitoringFinanceEntityCard
              key={entity.unitId}
              entity={entity}
            />
          ))}
        </div>
      ) : null}

      <div className={styles.articleList}>
        <h3 className={styles.sectionTitle}>
          {t('valueChainMap.monitoring.results.financeFindingsFeed')}
        </h3>
        {financeThemeOptions.length > 1 ? (
          <div className={styles.themeFilterRow}>
            {financeThemeOptions.map((theme) => (
              <button
                key={theme}
                type="button"
                className={clsx(styles.themeFilterChip, {
                  [styles.themeFilterChipActive]: financeThemeFilter === theme,
                })}
                onClick={() => setFinanceThemeFilter(theme)}
              >
                {theme === 'all'
                  ? t('valueChainMap.monitoring.results.financeAllThemes')
                  : t(`valueChainMap.monitoring.results.financeTheme.${theme}`, {
                    defaultValue: theme,
                  })}
              </button>
            ))}
          </div>
        ) : null}
        {filteredFinanceFindings.length
          ? filteredFinanceFindings.map((finding) => (
            <MonitoringFinanceFindingCard
              key={finding.id}
              finding={finding}
            />
          ))
          : (
            <p className={styles.stateMessage}>
              {t('valueChainMap.monitoring.results.noFinanceFindings')}
            </p>
          )}
      </div>
    </>
  );

  const renderOwnershipBody = () => (
    <>
      <MonitoringResultsSummary
        summary={isOwnershipMonitoringSignalResult(results) ? results.summary : {
          total_findings: 0,
          high_risk_count: 0,
          entities_monitored: 0,
        }}
        mode="ownership"
      />

      <div className={styles.analysisCtaCard}>
        <div className={styles.analysisCtaCopy}>
          <p className={styles.analysisCtaHint}>
            {t('valueChainMap.monitoring.results.ownershipOpenAnalysisHint')}
          </p>
          <button
            type="button"
            className={styles.analysisButtonPrimary}
            onClick={() => setIsOwnershipAnalysisOpen(true)}
            disabled={!ownershipEntities.length && !ownershipFindings.length}
          >
            {t('valueChainMap.monitoring.results.ownershipOpenAnalysis')}
          </button>
        </div>
      </div>

      <div
        className={styles.resultsTabs}
        role="tablist"
        aria-label={t('valueChainMap.monitoring.results.ownershipResultsTabsLabel')}
      >
        <button
          type="button"
          role="tab"
          aria-selected={ownershipResultsTab === 'profiles'}
          className={clsx(styles.resultsTab, {
            [styles.resultsTabActive]: ownershipResultsTab === 'profiles',
          })}
          onClick={() => setOwnershipResultsTab('profiles')}
        >
          {t('valueChainMap.monitoring.results.ownershipProfilesTab')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={ownershipResultsTab === 'findings'}
          className={clsx(styles.resultsTab, {
            [styles.resultsTabActive]: ownershipResultsTab === 'findings',
          })}
          onClick={() => setOwnershipResultsTab('findings')}
        >
          {t('valueChainMap.monitoring.results.ownershipFindingsTab')}
        </button>
      </div>

      {ownershipResultsTab === 'profiles' ? (
        <div className={styles.articleList} role="tabpanel">
          {ownershipEntities.length
            ? ownershipEntities.map((entity) => (
              <MonitoringOwnershipEntityCard
                key={entity.unitId}
                entity={entity}
              />
            ))
            : (
              <p className={styles.stateMessage}>
                {t('valueChainMap.monitoring.results.noOwnershipFindings')}
              </p>
            )}
        </div>
      ) : (
        <div className={styles.articleList} role="tabpanel">
          {ownershipThemeOptions.length > 1 ? (
            <div className={styles.themeFilterRow}>
              {ownershipThemeOptions.map((theme) => (
                <button
                  key={theme}
                  type="button"
                  className={clsx(styles.themeFilterChip, {
                    [styles.themeFilterChipActive]: ownershipThemeFilter === theme,
                  })}
                  onClick={() => setOwnershipThemeFilter(theme)}
                >
                  {theme === 'all'
                    ? t('valueChainMap.monitoring.results.ownershipAllThemes')
                    : t(`valueChainMap.monitoring.results.ownershipTheme.${theme}`, {
                      defaultValue: theme,
                    })}
                </button>
              ))}
            </div>
          ) : null}
          {filteredOwnershipFindings.length
            ? filteredOwnershipFindings.map((finding) => (
              <MonitoringOwnershipFindingCard
                key={finding.id}
                finding={finding}
              />
            ))
            : (
              <p className={styles.stateMessage}>
                {t('valueChainMap.monitoring.results.noOwnershipFindings')}
              </p>
            )}
        </div>
      )}

      <OwnershipAnalysisModal
        open={isOwnershipAnalysisOpen}
        results={isOwnershipMonitoringSignalResult(results) ? results : null}
        onClose={() => setIsOwnershipAnalysisOpen(false)}
        showImpactScenarioCta={showImpactScenarioCta}
        isImpactScenarioCreating={isImpactScenarioCreating}
        onCreateImpactScenario={onCreateImpactScenario}
      />
    </>
  );

  const renderClimateBody = () => (
    <>
      <MonitoringResultsSummary
        summary={isClimateMonitoringSignalResult(results) ? results.summary : {
          total_findings: 0,
          high_risk_count: 0,
          entities_monitored: 0,
        }}
        mode="climate"
      />

      {climateThemeOptions.length > 1 ? (
        <div className={styles.themeFilterRow}>
          {climateThemeOptions.map((theme) => (
            <button
              key={theme}
              type="button"
              className={clsx(styles.themeFilterChip, {
                [styles.themeFilterChipActive]: climateThemeFilter === theme,
              })}
              onClick={() => setClimateThemeFilter(theme)}
            >
              {theme === 'all'
                ? t('valueChainMap.monitoring.results.climateAllThemes')
                : t(`valueChainMap.monitoring.results.climateTheme.${theme}`, {
                  defaultValue: theme,
                })}
            </button>
          ))}
        </div>
      ) : null}

      <div className={styles.analysisCtaCard}>
        <div className={styles.analysisCtaCopy}>
          <p className={styles.analysisCtaHint}>
            {t('valueChainMap.monitoring.results.climateOpenAnalysisHint')}
          </p>
          <button
            type="button"
            className={styles.analysisButtonPrimary}
            onClick={() => setIsClimateAnalysisOpen(true)}
            disabled={!filteredClimateEntities.length && !filteredClimateFindings.length}
          >
            {t('valueChainMap.monitoring.results.climateOpenAnalysis')}
          </button>
        </div>
      </div>

      <div
        className={styles.resultsTabs}
        role="tablist"
        aria-label={t('valueChainMap.monitoring.results.climateResultsTabsLabel')}
      >
        <button
          type="button"
          role="tab"
          aria-selected={climateResultsTab === 'profiles'}
          className={clsx(styles.resultsTab, {
            [styles.resultsTabActive]: climateResultsTab === 'profiles',
          })}
          onClick={() => setClimateResultsTab('profiles')}
        >
          {t('valueChainMap.monitoring.results.climateProfilesTab')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={climateResultsTab === 'findings'}
          className={clsx(styles.resultsTab, {
            [styles.resultsTabActive]: climateResultsTab === 'findings',
          })}
          onClick={() => setClimateResultsTab('findings')}
        >
          {t('valueChainMap.monitoring.results.climateFindingsTab')}
        </button>
      </div>

      {climateResultsTab === 'profiles' ? (
        <div className={styles.articleList} role="tabpanel">
          {filteredClimateEntities.length
            ? filteredClimateEntities.map((entity) => (
              <MonitoringClimateEntityCard
                key={entity.unitId}
                entity={entity}
              />
            ))
            : (
              <p className={styles.stateMessage}>
                {t('valueChainMap.monitoring.results.noClimateFindings')}
              </p>
            )}
        </div>
      ) : (
        <div className={styles.articleList} role="tabpanel">
          {filteredClimateFindings.length
            ? filteredClimateFindings.map((finding) => (
              <MonitoringClimateFindingCard
                key={finding.id}
                finding={finding}
              />
            ))
            : (
              <p className={styles.stateMessage}>
                {t('valueChainMap.monitoring.results.noClimateFindings')}
              </p>
            )}
        </div>
      )}

      <ClimateAnalysisModal
        open={isClimateAnalysisOpen}
        results={isClimateMonitoringSignalResult(results) ? results : null}
        themeFilter={climateThemeFilter}
        onThemeFilterChange={setClimateThemeFilter}
        onClose={() => setIsClimateAnalysisOpen(false)}
        showImpactScenarioCta={showImpactScenarioCta}
        isImpactScenarioCreating={isImpactScenarioCreating}
        onCreateImpactScenario={onCreateImpactScenario}
      />
    </>
  );

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className={styles.loaderWrapper}>
          <SmallLoader size={30} />
        </div>
      );
    }

    if (isError) {
      return (
        <p className={styles.stateMessage}>{t('valueChainMap.monitoring.results.loadError')}</p>
      );
    }

    if (isEmpty || !results) {
      return (
        <p className={styles.stateMessage}>{t(emptyMessageKey)}</p>
      );
    }

    if (isFinanceMode && isFinanceMonitoringSignalResult(results)) {
      return renderFinanceBody();
    }

    if (isOwnershipMode && isOwnershipMonitoringSignalResult(results)) {
      return renderOwnershipBody();
    }

    if (isClimateMode && isClimateMonitoringSignalResult(results)) {
      return renderClimateBody();
    }

    return (
      <>
        <MonitoringResultsSummary
          summary={results.summary}
          mode={agentDefinition.resultsMode}
        />

        <div className={styles.articleList}>
          {isRegulationMode
            ? sortedRegulations.map((regulation) => (
              <MonitoringRegulationCard
                key={regulation.id}
                regulation={regulation}
              />
            ))
            : sortedArticles.map((article) => (
              <MonitoringArticleCard
                key={`${article.title}-${article.date}-${article.source}`}
                article={article}
              />
            ))}
        </div>
      </>
    );
  };

  return (
    <SidePanel
      open={open}
      className={styles.container}
      ariaModal
      role="dialog"
      ariaLabel={t(titleKey)}
    >
      <div className={styles.header}>
        <button
          type="button"
          className={styles.iconButton}
          onClick={onBack}
          aria-label={t('common.back')}
          data-i18n-key="common.back"
        >
          <ArrowLeftIcon aria-hidden="true" />
        </button>
        <div className={styles.headerText}>
          <span className={styles.headerTitle}>{t('valueChainMap.monitoringResultsTitle')}</span>
          {sessionTitle ? (
            <span className={styles.headerSubtitle}>{sessionTitle}</span>
          ) : null}
        </div>
        <button
          type="button"
          className={styles.iconButton}
          onClick={onClose}
          aria-label={t('common.close')}
          data-i18n-key="common.close"
        >
          <CloseIcon aria-hidden="true" />
        </button>
      </div>

      <div className={styles.body}>
        {renderBreadcrumb()}
        {renderBody()}
      </div>

      <div className={styles.footer}>
        {showImpactScenarioCta && onCreateImpactScenario ? (
          <div className={styles.impactBridgeSection}>
            <p className={styles.impactBridgeDescription}>
              {t(impactCtaDescriptionKey)}
            </p>
            <Button
              title={t(impactCtaKey, climateThemeFilter !== 'all' ? {
                theme: t(`valueChainMap.monitoring.results.climateTheme.${climateThemeFilter}`, {
                  defaultValue: climateThemeFilter,
                }),
              } : undefined)}
              labelKey={impactCtaKey}
              variant={BUTTON_VARIANT.primary}
              isLoading={isImpactScenarioCreating}
              disabled={
                isLoading
                || isImpactScenarioCreating
                || (isClimateMode && !(climateFilteredAtRiskUnitIds?.length))
              }
              onClick={() => {
                if (isClimateMode && climateFilteredAtRiskUnitIds?.length) {
                  onCreateImpactScenario(climateFilteredAtRiskUnitIds);

                  return;
                }

                onCreateImpactScenario();
              }}
              className={styles.impactBridgeButton}
            />
          </div>
        ) : null}
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
        >
          {t('common.close')}
        </button>
      </div>
    </SidePanel>
  );
}

export default MonitoringResultsPanel;
